"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Camera, AtSign, Play } from "lucide-react";
import type { SiteFooterContent } from "@/shared/types/site-shell-content";
const LOGO_URL = "/Nav-Logo.png";
interface SiteFooterProps {
  content: SiteFooterContent;
}
const SOCIAL_ICON_BY_TYPE = {
  email: AtSign,
  instagram: Camera,
  youtube: Play
} as const;
function FooterLink(t0) {
  const $ = _c(6);
  const {
    href,
    label
  } = t0;
  if (href.startsWith("mailto:")) {
    let t1;
    if ($[0] !== href || $[1] !== label) {
      t1 = <a href={href} className="transition-colors hover:text-white">{label}</a>;
      $[0] = href;
      $[1] = label;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== href || $[4] !== label) {
    t1 = <Link href={href} className="transition-colors hover:text-white">{label}</Link>;
    $[3] = href;
    $[4] = label;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function SiteFooter(t0) {
  const $ = _c(46);
  const {
    content
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      target: containerRef,
      offset: ["start end", "end end"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [0, 1];
    t3 = [150, 0];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  const textY = useTransform(scrollYProgress, t2, t3);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [0, 1];
    t5 = [0, 1];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  const textOpacity = useTransform(scrollYProgress, t4, t5);
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Image alt="ItsHealer" className="h-auto w-auto brightness-0 invert" height={42} src={LOGO_URL} width={120} />;
    t7 = <div className="flex items-center gap-2" />;
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== content.contactLines) {
    t8 = content.contactLines.map(_temp);
    $[7] = content.contactLines;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t6}{t7}<div className="space-y-2 text-[15px] leading-relaxed text-[#A4A4A4]">{t8}</div></div>;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== content.sections) {
    t10 = content.sections.map(_temp3);
    $[11] = content.sections;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10) {
    t11 = <div className="grid grid-cols-2 gap-16 sm:grid-cols-3 md:gap-24">{t10}</div>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9) {
    t12 = <div className="flex flex-col justify-between gap-16 pb-24 lg:flex-row">{t9}{t11}</div>;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="h-px w-full bg-white/20" />;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== content.ctaHref || $[20] !== content.ctaLabel) {
    t14 = <div className="relative w-full">{t13}<Link className="hover:bg-opacity-80 absolute -top-5 right-0 z-10 inline-flex cursor-pointer items-center rounded-full bg-[#7DE2F8] px-6 py-2.5 text-sm font-medium text-[#1D4263] shadow-lg transition-colors" href={content.ctaHref}>{content.ctaLabel}</Link></div>;
    $[19] = content.ctaHref;
    $[20] = content.ctaLabel;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== content.description) {
    t15 = <p className="max-w-[400px] leading-relaxed">{content.description}</p>;
    $[22] = content.description;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== content.legalLinks) {
    t16 = content.legalLinks.map(_temp4);
    $[24] = content.legalLinks;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t16) {
    t17 = <div className="flex items-start gap-8 text-[11px] font-medium tracking-widest uppercase md:items-center">{t16}</div>;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t15 || $[29] !== t17) {
    t18 = <div className="flex flex-col justify-between gap-8 pt-10 pb-6 text-[13px] text-[#A4A4A4] md:flex-row">{t15}{t17}</div>;
    $[28] = t15;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t12 || $[32] !== t14 || $[33] !== t18) {
    t19 = <div className="mx-auto max-w-[1400px] px-6 lg:px-12">{t12}{t14}{t18}</div>;
    $[31] = t12;
    $[32] = t14;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== textOpacity || $[36] !== textY) {
    t20 = {
      y: textY,
      opacity: textOpacity
    };
    $[35] = textOpacity;
    $[36] = textY;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== content.wordmark) {
    t21 = <h1 className="-mb-4 w-full overflow-hidden text-[20vw] leading-[0.75] font-bold tracking-tighter whitespace-nowrap text-white/5">{content.wordmark}</h1>;
    $[38] = content.wordmark;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20 || $[41] !== t21) {
    t22 = <div className="pointer-events-none mt-12 flex w-full items-end justify-center overflow-hidden select-none"><motion.div style={t20} className="w-full text-center">{t21}</motion.div></div>;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t19 || $[44] !== t22) {
    t23 = <footer ref={containerRef} className="relative overflow-hidden bg-[#155A7D] pt-20 text-[#e0e0e0]">{t19}{t22}</footer>;
    $[43] = t19;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  return t23;
}
function _temp4(link_0) {
  return <FooterLink href={link_0.href} key={link_0.label} label={link_0.label} />;
}
function _temp3(section) {
  return <div key={section.title}><h4 className="mb-8 text-[11px] font-semibold tracking-wider text-[#A4A4A4] uppercase">{section.title}</h4><ul className="flex flex-col gap-5 text-[15px]">{section.links.map(_temp2)}</ul></div>;
}
function _temp2(link) {
  return <li key={link.label}><FooterLink href={link.href} label={link.label} /></li>;
}
function _temp(line) {
  return <p key={line}>{line}</p>;
}